import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

/**
 * The footer's measurements and finish.
 *
 * The header got these first and the footer went without, so a brand could
 * tune the top of every page and not the bottom of it — and a footer with no
 * measurements of its own is the same block on every site.
 *
 * `minColumnWidth` is the one worth explaining rather than only labelling:
 * it is what decides when a row of four columns becomes two, and then one.
 * Nothing else here changes how the footer behaves on a phone.
 */

const DEFAULTS = {
  maxWidth: 1200, gutter: 24, padding: 28, columnGap: 40, linkGap: 10,
  fontSize: 13, align: 'left', minColumnWidth: 180, border: false,
  glass: { enabled: false, blur: 12, amount: 60 },
};

const Row = ({ label, hint, value, unit = 'px', min, max, step = 1, onChange }) => (
  <Box>
    <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
      <Typography variant='caption' color='text.secondary'>{label}</Typography>
      <Typography variant='caption' color='text.secondary'>{value}{unit}</Typography>
    </Stack>
    <Slider
      size='small'
      min={min}
      max={max}
      step={step}
      value={Number(value)}
      onChange={(_e, v) => onChange(v)}
      valueLabelDisplay='auto'
    />
    {hint && (
      <Typography variant='caption' color='text.secondary' display='block' mt='-4px'>
        {hint}
      </Typography>
    )}
  </Box>
);

export default function FooterStyleControls({ style, onChange }) {
  const s = { ...DEFAULTS, ...(style || {}), glass: { ...DEFAULTS.glass, ...((style || {}).glass || {}) } };
  const set = (patch) => onChange({ ...s, ...patch });
  const setGlass = (patch) => onChange({ ...s, glass: { ...s.glass, ...patch } });

  return (
    <Stack spacing='12px'>
      <Row label='Distance from the sides' value={s.gutter} min={0} max={120} onChange={(v) => set({ gutter: v })} />
      <Row
        label='Widest the contents get'
        hint={s.maxWidth === 0 ? 'Edge to edge.' : 'Centred once the window is wider.'}
        value={s.maxWidth} min={0} max={2000} step={20}
        onChange={(v) => set({ maxWidth: v })}
      />
      <Row label='Space above and below' value={s.padding} min={8} max={120} onChange={(v) => set({ padding: v })} />
      <Row label='Between the columns' value={s.columnGap} min={8} max={120} onChange={(v) => set({ columnGap: v })} />
      <Row label='Between the links' value={s.linkGap} min={0} max={48} onChange={(v) => set({ linkGap: v })} />
      <Row label='Text size' value={s.fontSize} min={11} max={20} onChange={(v) => set({ fontSize: v })} />
      <Row
        label='Narrowest a column gets'
        hint='Below this the row drops to fewer columns — and to one on a phone.'
        value={s.minColumnWidth} min={120} max={400} step={10}
        onChange={(v) => set({ minColumnWidth: v })}
      />

      <FormControl size='small' fullWidth>
        <InputLabel>Line things up</InputLabel>
        <Select label='Line things up' value={s.align} onChange={(e) => set({ align: e.target.value })}>
          <MenuItem value='left'>To the left</MenuItem>
          <MenuItem value='center'>Centred</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={<Switch size='small' checked={s.border} onChange={(e) => set({ border: e.target.checked })} />}
        label={<Typography variant='body2'>Hairline above</Typography>}
      />

      <FormControlLabel
        control={
          <Switch size='small' checked={s.glass.enabled} onChange={(e) => setGlass({ enabled: e.target.checked })} />
        }
        label={<Typography variant='body2'>Frosted glass</Typography>}
      />
      {s.glass.enabled && (
        <>
          <Row
            label='How solid' unit='%' value={s.glass.amount} min={0} max={100}
            hint='0 is clear glass, 100 is the plain colour.'
            onChange={(v) => setGlass({ amount: v })}
          />
          <Row label='Blur' value={s.glass.blur} min={0} max={40} onChange={(v) => setGlass({ blur: v })} />
        </>
      )}
    </Stack>
  );
}
